/** The console's Chinese names for server vocabularies (書面語; the raw value stays on hover where useful). */
import type { AdminRole, JobState, LedgerRow, PersonalChapterRow, ReportState, RevenueSourceKey, Segment, UserStatus } from '../api/types';
import type { Tone } from '../ui';

export const ROLE_LABEL: Record<AdminRole, string> = { viewer: '檢視', support: '客服', moderator: '審核', content: '內容編輯', owner: '擁有者' };
export const ROLE_HINT: Record<AdminRole, string> = {
  viewer: '只可查看總覽、Jobs、故事與漫畫',
  support: '查看玩家，調整章節券、墨幣、會籍，重試故事與漫畫，個別推送',
  moderator: '客服權限，加上處理檢舉、停用或封禁帳戶',
  content: '審核權限，加上編輯及發佈內容',
  owner: '全部權限：App 設定、功能開關、廣播推送、團隊、刪除帳戶',
};

export const USER_STATUS: Record<UserStatus, { label: string; tone: Tone }> = {
  active: { label: '正常', tone: 'ok' },
  suspended: { label: '已停用', tone: 'danger' },
  deleting: { label: '刪除中', tone: 'warn' },
  deleted: { label: '已刪除', tone: 'neutral' },
};

export const CHAPTER_STATUS: Record<PersonalChapterRow['status'], { label: string; tone: Tone }> = {
  planned: { label: '已排程', tone: 'neutral' },
  generating: { label: '撰寫中', tone: 'info' },
  ready: { label: '待玩', tone: 'accent' },
  played: { label: '已玩', tone: 'ok' },
  failed: { label: '失敗', tone: 'danger' },
};

export const JOB_STATE: Record<JobState, { label: string; tone: Tone }> = {
  queued: { label: '排隊中', tone: 'neutral' },
  running: { label: '執行中', tone: 'info' },
  retry_scheduled: { label: '等待重試', tone: 'warn' },
  succeeded: { label: '成功', tone: 'ok' },
  failed_permanent: { label: '永久失敗', tone: 'danger' },
  cancelled: { label: '已取消', tone: 'neutral' },
};

export const LEDGER_KIND: Record<LedgerRow['kind'], string> = {
  grant: '贈送',
  purchase: '購買',
  reserve: '預留',
  settle: '結算',
  release: '退回預留',
  refund: '退款',
  adjust: '人手調整',
  reward: '獎勵',
};

export const STAGE_LABEL: Record<string, string> = {
  stranger: '陌生',
  acquainted: '認識',
  familiar: '熟悉',
  trusted: '信任',
  close: '親近',
  dating: '交往中',
  partner: '戀人',
};

export const TIER_LABEL: Record<'basic' | 'illustrated', string> = { basic: '基本版', illustrated: '繪製版' };

export const REPORT_STATE: Record<ReportState, string> = { open: '待處理', actioned: '已處理', dismissed: '已駁回' };

export const REPORT_REASON: Record<string, string> = {
  harassment: '騷擾',
  sexual_content: '色情內容',
  minor_safety: '未成年人安全',
  hate: '仇恨言論',
  spam: '垃圾訊息',
  wrong_character: '角色不符',
  story_mismatch: '故事不連貫',
  art_quality: '畫質問題',
  other: '其他',
};

export const SEGMENT_LABEL: Record<Segment, string> = {
  active7: '最近 7 日活躍',
  all: '全部正常帳戶',
  plus: '付費會員（月卡 / Plus / Premium）',
  zh: '中文玩家',
  en: '英文玩家',
};

export const REVENUE_SOURCE: Record<RevenueSourceKey, { label: string; test: boolean }> = {
  app_store: { label: 'App 內購', test: false },
  stripe: { label: '網站 Stripe', test: false },
  app_store_sandbox: { label: 'App sandbox', test: true },
  stripe_test: { label: 'Stripe 測試', test: true },
};

export const THREAD_STATUS: Record<'open' | 'paid' | 'expired', { label: string; tone: Tone }> = {
  open: { label: '未完', tone: 'accent' },
  paid: { label: '已收線', tone: 'ok' },
  expired: { label: '已過期', tone: 'neutral' },
};

/** AI usage `purpose` (ai_usage_records); an unknown one shows as its key. */
export const AI_PURPOSE: Record<string, string> = {
  story: 'AI 章節撰寫',
  storyboard: '漫畫分鏡',
  balloons: '漫畫對白框配對',
  placement: '漫畫對白框位置',
  narration: '旁白',
  intent: '意圖判斷',
  chat: '角色對話',
  memory: '角色記憶',
  moderation: '內容審查',
  tts: '語音',
  'image.page': '漫畫頁',
  'image.panel': '漫畫分格',
  'image.cover': '封面',
  'image.asset': '美術素材',
};
